import { useEffect, useRef } from 'react';

/**
 * Land the caret at the top of what was just navigated to (SPEC-007 D-6).
 *
 * `useScrollReset` moves the PAGE and nothing moves focus, which leaves the two
 * disagreeing about where the reader is. A tap on a companion four fifths of the
 * way down leaves focus on that card, so the next Tab carries on from a control
 * that is no longer on the screen; a swipe activates nothing at all, so focus
 * stays on whatever was last touched — or on nothing.
 *
 * Never on first mount. A page that focuses itself as it loads takes focus off
 * the URL bar and re-announces a document the reader has only just been handed;
 * only a navigation between plants is a new page arriving.
 *
 * @param {*} key - what was navigated to. Focus moves when this changes.
 * @returns {import('react').RefObject} attach to the top of the page's content.
 */
export default function useFocusReset(key) {
    const targetRef = useRef(null);
    const landedOnRef = useRef(key);

    useEffect(() => {
        if (landedOnRef.current === key) {
            return;
        }

        landedOnRef.current = key;
        // `preventScroll`, because the scroll reset has already put the page at
        // its top and the browser's own scroll-into-view would fight it — the
        // target is a container, so "into view" means its far edge just as
        // readily as its near one.
        targetRef.current?.focus({ preventScroll: true });
    }, [key]);

    return targetRef;
}
